import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { MenuBook as PlaybookIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';

/**
 * Dialog listing the company's playbooks; calls onApply(playbookId) for the chosen one.
 * Props: open, onClose, onApply, applied (array of playbook names already on the case), applying
 */
export const PlaybookPicker = ({ open, onClose, onApply, applied = [], applying = false }) => {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSelected(null);
    apiService.workflows.get()
      .then((res) => { if (!cancelled) setPlaybooks(res.data?.playbooks || []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load playbooks'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle display="flex" alignItems="center" gap={1}>
        <PlaybookIcon color="primary" />
        Apply playbook
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : playbooks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No playbooks defined. Create one under Settings &gt; Playbooks &amp; SLA.
          </Typography>
        ) : (
          <List disablePadding>
            {playbooks.map((pb) => {
              const alreadyApplied = applied.includes(pb.name);
              const taskCount = (pb.tasks || []).length;
              return (
                <ListItemButton
                  key={pb.id}
                  selected={selected === pb.id}
                  onClick={() => setSelected(pb.id)}
                  disabled={applying}
                  sx={{ borderRadius: 1, mb: 0.5, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle2">{pb.name}</Typography>
                        {pb.category && <Chip label={pb.category} size="small" variant="outlined" />}
                        <Chip label={`${taskCount} task${taskCount === 1 ? '' : 's'}`} size="small" color="primary" variant="outlined" />
                        {alreadyApplied && <Chip label="Applied" size="small" color="success" />}
                      </Box>
                    }
                    secondary={pb.description || null}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        {selected && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Tasks whose titles already exist on the case are skipped.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>Cancel</Button>
        <Button variant="contained" onClick={() => onApply(selected)} disabled={!selected || applying}>
          {applying ? 'Applying...' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaybookPicker;
